import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import { toast } from "react-toastify";
import {
  Search,
  Send,
  MessageSquare,
  Phone,
  CheckCircle,
  AlertCircle,
  History,
  X,
  Users,
} from "lucide-react";
import WorkspaceTitleHeader from "../../components/common/WorkspaceTitleHeader";

const WALLET_SUMMARY_ENDPOINT = "/payment/wallet";

const unwrapResponse = (res) => {
  const body = res?.data ?? {};
  const ok = typeof body.ok === "boolean" ? body.ok : true;
  const data = body?.data ?? body;
  return { ok, data };
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

// 🎨 Helper for avatar colors
const getAvatarColor = (name) => {
  const colors = [
    "bg-emerald-100 text-emerald-700",
    "bg-blue-100 text-blue-700",
    "bg-violet-100 text-violet-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
    "bg-cyan-100 text-cyan-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  return name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

// 📦 Token-style phone number input
function PhoneNumberInput({ numbers, setNumbers }) {
  const [input, setInput] = useState("");

  const handleInputChange = (e) => {
    const value = e.target.value;
    const lastChar = value.slice(-1);
    const isSeparator = /[\n, ]/.test(lastChar);

    if (isSeparator) {
      tryAddPhone(input.trim());
      setInput("");
    } else {
      setInput(value);
    }
  };

  const tryAddPhone = (value) => {
    let normalized = value.replace(/\D/g, "");

    // Auto-add +91 if it's a 10-digit number and no +
    if (/^\d{10}$/.test(normalized)) {
      normalized = "91" + normalized;
    }

    // Accept if it's a valid E.164 format (10–15 digits with optional +)
    if (/^\d{10,15}$/.test(normalized)) {
      if (!numbers.includes(normalized)) {
        setNumbers([...numbers, normalized]);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      tryAddPhone(input.trim());
      setInput("");
      e.preventDefault();
    } else if (e.key === "Backspace" && input === "") {
      setNumbers(numbers.slice(0, -1));
    }
  };

  const removeNumber = (num) => {
    setNumbers(numbers.filter((n) => n !== num));
  };

  return (
    <div className="min-h-[50px] w-full cursor-text flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-2 transition-all focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-500/15">
      {numbers.map((num, idx) => (
        <span
          key={idx}
          className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm"
        >
          {num}
          <button
            type="button"
            className="text-slate-400 transition-colors hover:text-red-500"
            onClick={() => removeNumber(num)}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        type="text"
        className="min-w-[120px] flex-grow bg-transparent p-1 text-sm text-slate-700 outline-none placeholder:text-slate-400"
        placeholder="Type number & press Enter"
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

export default function SendTextMessagePage() {
  const [manualNumbers, setManualNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [walletSummary, setWalletSummary] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [messageLogs, setMessageLogs] = useState([]);
  const [saveContact, setSaveContact] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [manualContactName, setManualContactName] = useState("");
  const auth = useAuth();
  const businessId = auth?.effectiveBusinessId || auth?.selectedBusinessId || auth?.businessId;
  const [senders, setSenders] = useState([]);
  const [senderId, setSenderId] = useState("");
  const selectedSender = senders.find(sender => sender.phoneNumberId === senderId);

  useEffect(() => {
    let disposed = false;
    setSenders([]);
    setSenderId("");
    if (!businessId) return undefined;
    axiosClient.get(`WhatsAppSettings/senders/${businessId}`).then(response => {
      if (disposed) return;
      const rows = Array.isArray(response?.data) ? response.data : response?.data?.items || [];
      const available = rows.map(sender => ({
        provider: String(sender.provider ?? sender.Provider ?? "").trim().toUpperCase(),
        phoneNumberId: String(sender.phoneNumberId ?? sender.PhoneNumberId ?? "").trim(),
        displayNumber: sender.whatsAppBusinessNumber || sender.WhatsAppBusinessNumber || sender.phoneNumberId,
        isDefault: sender.isDefault ?? sender.IsDefault,
      })).filter(sender => sender.provider === "META_CLOUD" && sender.phoneNumberId);
      setSenders(available);
      setSenderId((available.find(sender => sender.isDefault) || available[0])?.phoneNumberId || "");
    }).catch(() => { if (!disposed) toast.error("Failed to load WhatsApp senders."); });
    return () => { disposed = true; };
  }, [businessId]);

  const navigate = useNavigate();

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const res = await axiosClient.get("/contacts/");
        setContacts(res.data?.data?.items || []);
      } catch (err) {
        console.error("❌ Error loading contacts", err);
        toast.error("Failed to load contacts.");
      }
    };

    const loadHistory = async () => {
      try {
        const res = await axiosClient.get(
          "/reporting/messages/recent?limit=10"
        );
        setMessageLogs(res.data?.data || []);
      } catch (err) {
        console.error("❌ Error loading history", err);
      }
    };

    const loadWalletSummary = async () => {
      try {
        setWalletLoading(true);
        const res = await axiosClient.get(WALLET_SUMMARY_ENDPOINT);
        const { ok, data } = unwrapResponse(res);
        setWalletSummary(ok ? data : null);
      } catch {
        setWalletSummary(null);
      } finally {
        setWalletLoading(false);
      }
    };

    loadContacts();
    loadHistory();
    loadWalletSummary();
  }, [submitting]); // Reload history after sending

  const numbers = [...new Set([...manualNumbers, ...selectedNumbers])];

  const digitsOnly = (val) => String(val || "").replace(/\D/g, "");

  const getContactDisplayName = (contact) => {
    const name = String(contact?.name || "").trim();
    const profileName = String(
      contact?.profileName || contact?.ProfileName || ""
    ).trim();

    if (name && name !== "WhatsApp User") return name;
    if (profileName) return profileName;
    return name || "Unknown";
  };

  const findContactByNumber = (num) => {
    const targetDigits = digitsOnly(num);
    return contacts.find((c) => digitsOnly(c?.phoneNumber) === targetDigits);
  };

  const resolveContactNameForSave = (num) => {
    const contact = findContactByNumber(num);
    const displayName = getContactDisplayName(contact);
    if (
      displayName &&
      displayName !== "WhatsApp User" &&
      displayName !== "Unknown"
    ) {
      return displayName;
    }

    if (
      manualNumbers.length === 1 &&
      digitsOnly(manualNumbers[0]) === digitsOnly(num)
    ) {
      const manual = manualContactName.trim();
      return manual || undefined;
    }

    return undefined;
  };

  const handleSend = async () => {
    if (!selectedSender) { toast.warn("Select a WhatsApp sender."); return; }
    if (!message || numbers.length === 0) {
      toast.warn("⚠️ Please enter a message and at least one valid number.");
      return;
    }

    setSubmitting(true);
    let success = 0,
      failed = 0;

    for (const number of numbers) {
      try {
        const contactName =
          saveContact ? resolveContactNameForSave(number) : undefined;
        const res = await axiosClient.post(
          "/messageengine/send-contentfree-text",
          {
            recipientNumber: number,
            phoneNumberId: selectedSender.phoneNumberId,
            provider: selectedSender.provider,
            contactId: findContactByNumber(number)?.id,
            textContent: message,
            isSaveContact: saveContact,
            contactName,
          }
        );
        // Sometimes backend returns specific structure, fallback to typical checks
        if (res.data?.success || res.status === 200) {
            success++
        } else {
            failed++
        }
      } catch (err) {
        failed++;
        console.error("Send failed:", number, err);
      }
    }

    toast.success(`✅ Sent: ${success}, ❌ Failed: ${failed}`);
    setSubmitting(false);
    setMessage("");
    setManualNumbers([]);
    setSelectedNumbers([]);
    setManualContactName("");
  };

  // Filter contacts
  const filteredContacts = contacts.filter(
    (c) =>
      getContactDisplayName(c).toLowerCase().includes(contactSearch.toLowerCase()) ||
      String(c.phoneNumber || "").includes(contactSearch)
  );
  const showWalletInfo = walletLoading || !!walletSummary;
  const walletToneClassName = walletSummary?.isNegativeBalance
    ? "border-rose-200 bg-rose-50/80 text-rose-800"
    : walletSummary?.isLowBalance
      ? "border-amber-200 bg-amber-50/80 text-amber-800"
      : "border-slate-200 bg-slate-50/80 text-slate-600";
  const walletValueClassName = walletSummary?.isNegativeBalance
    ? "text-rose-900"
    : walletSummary?.isLowBalance
      ? "text-amber-900"
      : "text-slate-900";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100 px-8 pb-6 pt-6 font-sans sm:px-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">

        {/* Header */}
        <WorkspaceTitleHeader
          icon={Send}
          title="Send Direct Message"
          subtitle="Compose and send instant text messages to your contacts or new numbers."
          actions={
            <button
              onClick={() => navigate("/app/campaigns/messagelogs")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
            >
              <History size={16} />
              View Full History
            </button>
          }
        />

        <label className="mb-4 block text-sm font-medium text-slate-700">
          WhatsApp sender
          <select aria-label="WhatsApp sender" value={senderId} onChange={event => setSenderId(event.target.value)}
            disabled={submitting || !senders.length} className="mt-1 block w-full rounded-lg border border-slate-200 p-2">
            {!senders.length && <option value="">No connected WhatsApp sender</option>}
            {senders.map(sender => <option key={sender.phoneNumberId} value={sender.phoneNumberId}>
              {sender.displayNumber || sender.phoneNumberId}
            </option>)}
          </select>
        </label>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          
          {/* 👈 Left Column: Recipients */}
          <div className="lg:col-span-4 space-y-4">
            
            {/* 📇 Contact Selector Card */}
            <div className="flex h-[420px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Users size={18} className="text-emerald-600" />
                    Select Contacts
                  </h3>
                  </div>
                  <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                    {selectedNumbers.length} selected
                  </span>
                </div>
                {/* Search Input */}
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search name or number..."
                    className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm transition-all focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Contact List */}
              <div className="custom-scrollbar flex-1 space-y-1 overflow-y-auto p-2">
                {filteredContacts.length > 0 ? (
                  filteredContacts.map((contact) => {
                    const isSelected = selectedNumbers.includes(contact.phoneNumber);
                    return (
                      <label
                        key={contact.id}
                          className={`group flex cursor-pointer items-center gap-3 rounded-xl border border-transparent p-2.5 transition-all ${
                            isSelected
                              ? "border-emerald-100 bg-emerald-50"
                              : "hover:bg-slate-50"
                          }`}
                      >
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={isSelected}
                            onChange={(e) => {
                              const value = contact.phoneNumber;
                              setSelectedNumbers((prev) =>
                                e.target.checked
                                  ? [...prev, value]
                                  : prev.filter((n) => n !== value)
                              );
                            }}
                          />
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                              isSelected
                                ? "bg-emerald-500 text-white shadow-md scale-105"
                                : getAvatarColor(getContactDisplayName(contact) || "?")
                            }`}
                          >
                            {isSelected ? (
                              <CheckCircle size={16} />
                            ) : (
                              getInitials(getContactDisplayName(contact) || "?")
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                              className={`truncate text-sm font-medium ${
                                isSelected ? "text-emerald-900" : "text-slate-700"
                              }`}
                          >
                            {getContactDisplayName(contact)}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {contact.phoneNumber}
                          </p>
                        </div>
                      </label>
                    );
                  })
                ) : (
                  <div className="flex h-32 flex-col items-center justify-center text-slate-400">
                    <Search size={32} className="mb-2 opacity-50" />
                    <p className="text-sm">No contacts found</p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* 👉 Right Column: Composer & History */}
          <div className="lg:col-span-8 space-y-4">

            {/* ✍️ Manual Numbers Card */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3">
                <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <Phone size={18} className="text-emerald-600" />
                Manual Entry
                </h3>
              </div>
              <PhoneNumberInput
                numbers={manualNumbers}
                setNumbers={setManualNumbers}
              />
              <div className="mt-3 flex items-start gap-2">
                <div className="relative flex items-center h-5">
                  <input
                    id="saveContact"
                    type="checkbox"
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    checked={saveContact}
                    onChange={(e) => setSaveContact(e.target.checked)}
                  />
                </div>
                <label htmlFor="saveContact" className="cursor-pointer select-none text-xs leading-tight text-slate-500">
                  Automatically save new manual numbers to your Contacts list after sending.
                </label>
              </div>

              {saveContact && manualNumbers.length === 1 && numbers.length === 1 && (
                <div className="mt-3">
                  <label className="mb-1 block text-xs text-slate-500">
                    Contact name (optional)
                  </label>
                  <input
                    type="text"
                    value={manualContactName}
                    onChange={(e) => setManualContactName(e.target.value)}
                    placeholder="Leave empty to use WhatsApp profile name if available"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm transition-all focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
                  />
                </div>
              )}
            </div>
             
            {/* 💬 Composer Card */}
            <div className="relative rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="pointer-events-none absolute right-0 top-0 p-5 opacity-10">
                <MessageSquare size={96} />
              </div>
              <div className="relative z-10 mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Message composer
                  </div>
                  <h3 className="mt-1 flex items-center gap-2 font-semibold text-slate-800">
                    <MessageSquare size={18} className="text-emerald-600" />
                    Compose Message
                  </h3>
                </div>
                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                  {numbers.length} recipients
                </div>
              </div>
               
              <div className="relative z-10">
                <textarea
                  rows={4}
                  className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-700 shadow-sm transition-all placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
                  placeholder="Type your message here... (Hit Shift+Enter for new line)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                
                <div className="mt-2.5 flex items-center justify-between text-xs text-slate-400">
                   <span>
                     Format: 
                     <span className="font-mono mx-1">*bold*</span>
                    <span className="font-mono mx-1">_italic_</span>
                    <span className="font-mono mx-1">~strike~</span>
                   </span>
                   <span className={message.length > 1000 ? "text-amber-500" : ""}>
                     {message.length} characters
                   </span>
                </div>
              </div>

              <div className="relative z-10 mt-4 flex items-center justify-end gap-3">
                {showWalletInfo ? (
                  <div className={`mr-auto rounded-xl border px-3 py-2 text-xs ${walletToneClassName}`}>
                    {walletLoading ? (
                      <div className="text-slate-500">Loading wallet…</div>
                    ) : (
                      <div className="space-y-1">
                        <div>
                          Available balance:{" "}
                          <span className={`font-semibold ${walletValueClassName}`}>
                            {formatCurrency(walletSummary?.availableBalance ?? 0)}
                          </span>
                        </div>
                        {walletSummary?.isNegativeBalance ? (
                          <div className="font-medium text-rose-800">Wallet balance is negative</div>
                        ) : walletSummary?.isLowBalance ? (
                          <div className="font-medium text-amber-800">Low wallet balance</div>
                        ) : null}
                        <div>Conversation charges may apply</div>
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="text-right">
                   <p className="mb-1 text-xs text-slate-500">
                     Total Recipients: <strong className="text-slate-900">{numbers.length}</strong>
                   </p>
                </div>
                <button
                  onClick={handleSend}
                  disabled={submitting || numbers.length === 0 || !message || !selectedSender}
                  className={`flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold transition-all active:scale-95 ${
                    submitting || numbers.length === 0 || !message
                      ? "cursor-not-allowed bg-slate-100 text-slate-400"
                      : "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200 hover:from-emerald-600 hover:to-teal-700"
                  }`}
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* 🕰 Timeline History */}
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4">
              <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <History size={18} className="text-emerald-600" />
                Recent Activity
              </h3>
              </div>
               
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="w-10 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        #
                      </th>
                      <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Recipient
                      </th>
                      <th className="w-1/2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Message
                      </th>
                       <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {messageLogs.length > 0 ? (
                      messageLogs.map((log, index) => {
                        const status = log.status?.toLowerCase() || "";
                        const isFailure = ["failed", "undelivered", "rejected", "error"].some(s => status.includes(s));
                        
                        return (
                          <tr key={log.id} className="group text-sm transition-colors hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 text-center">
                               <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isFailure ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                 {isFailure ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
                               </div>
                            </td>
                            <td className="px-3 py-2.5 font-medium text-slate-900">
                              {log.recipientNumber}
                            </td>
                            <td className="max-w-xs truncate px-3 py-2.5 text-slate-600" title={log.messageContent}>
                               {log.messageContent || <span className="italic text-slate-400">No content</span>}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide ${isFailure ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                                {log.status || "Unknown"}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs text-slate-500">
                              {log.sentAt ? new Date(log.sentAt).toLocaleString() : "Just now"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-10 text-center text-slate-400">
                          <div className="flex flex-col items-center justify-center gap-2">
                             <History size={24} className="opacity-30" />
                             <p>No recent message history</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
          </div>
          
        </div>
      </div>
    </div>
  );
}
